import React, { useCallback, useRef } from 'react';

import AppShell from 'app/AppShell';
import PremiumStepper from 'shared/components/PremiumStepper';
import StepTransition from 'shared/components/StepTransition';
import { afterPaint, scrollToFirstInvalidField } from 'shared/lib/scroll';
import OnboardingSplitLayout from 'features/onboarding/components/OnboardingSplitLayout';
import { useOnboarding } from 'features/onboarding/context/OnboardingContext';
import { StepNavProvider } from 'features/onboarding/context/StepNavContext';

import Step2Contact from 'features/onboarding/steps/Step2Contact';
import Step3Business from 'features/onboarding/steps/Step3Business';
import StepPayment from 'features/onboarding/steps/StepPayment';
import Step6Representative from 'features/onboarding/steps/Step6Representative';
import Step8Review from 'features/onboarding/steps/Step8Review';
import Step9Success from 'features/onboarding/steps/Step9Success';
import Step10FinalDetails from 'features/onboarding/steps/Step10FinalDetails';

// Linear 6-step flow (the landing page lives on its own `/` route). Documents
// are uploaded inline within the step that consumes them.
//
//   2. Contact               (trade name + contact person + mobile + map pin, SMS gated)
//   3. Business              (category/store type + kosher/delivery + email + hours)
//   4. Payment               (Osek + tax-clearance + bank uploads → OCR prefill)
//   5. Representative        (+ notes)
//   6. Visuals               (menu source + photo shoot + logo)
//   7. Review
//   8. Success
const RENDERERS: Record<number, React.FC> = {
  2: Step2Contact,
  3: Step3Business,
  4: StepPayment,
  5: Step6Representative,
  6: Step10FinalDetails,
  7: Step8Review,
  8: Step9Success,
};

// The review step is a wide dashboard-style layout; success is a full-screen
// celebration. Both opt out of the two-column split.
const WIDE_STEPS = new Set([7]);

// Ordered step keys for the visible progress track (steps 2..7).
const STEP_KEYS = [
  'contact',
  'business',
  'payment',
  'representative',
  'visuals',
  'review',
];

const TRACK_FROM = 2;
const TRACK_TO = 7;
const TRACK_TOTAL = TRACK_TO - TRACK_FROM + 1;

const OnboardingPage: React.FC = () => {
  const { data, setStep } = useOnboarding();
  const currentStep = data.metadata.currentStep;
  const Current = RENDERERS[currentStep] || Step2Contact;
  const isWide = WIDE_STEPS.has(currentStep);
  const isSuccess = currentStep === 8;
  const scrollAreaRef = useRef<HTMLElement>(null);

  // A forward jump the step guard rejects leaves the partner on the same step,
  // so point them at the field that blocked it.
  const handleStepSelect = useCallback(
    async (step: number) => {
      const moved = await setStep(step);
      if (moved) return;
      afterPaint(() => scrollToFirstInvalidField(scrollAreaRef.current));
    },
    [setStep],
  );

  const progressBar = (
    <PremiumStepper
      current={currentStep}
      maxReached={data.metadata.maxStepReached}
      total={TRACK_TOTAL}
      trackFromStep={TRACK_FROM}
      trackToStep={TRACK_TO}
      stepKeys={STEP_KEYS}
      onStepSelect={handleStepSelect}
    />
  );

  // Success screen: full-screen, no chrome.
  if (isSuccess) {
    return (
      <AppShell>
        <StepTransition stepKey={currentStep}>
          <Current />
        </StepTransition>
      </AppShell>
    );
  }

  // Review step: keep the wide dashboard layout with a slim sticky progress bar.
  if (isWide) {
    return (
      <AppShell
        wide
        stickyBar={
          <div className="mx-auto max-w-3xl px-4 py-3 sm:px-6">{progressBar}</div>
        }
      >
        <StepTransition stepKey={currentStep}>
          <Current />
        </StepTransition>
      </AppShell>
    );
  }

  // Form steps (2..6): two-column split — image on the left, form on the right.
  // StepNavProvider lets each step publish its Back / Next controls to the
  // layout's fixed footer.
  return (
    <StepNavProvider>
      <OnboardingSplitLayout
        progressBar={progressBar}
        scrollAreaRef={scrollAreaRef}
      >
        <StepTransition stepKey={currentStep}>
          <Current />
        </StepTransition>
      </OnboardingSplitLayout>
    </StepNavProvider>
  );
};

export default OnboardingPage;
